/**
 * THE quote / sales-order status vocabulary. Phase 3a · U5.
 *
 * ===========================================================================
 * THIS IS AUTHORED. FIGMA DREW NO QUOTE SCREEN.
 * ===========================================================================
 * `domain/stages.ts` transcribes a design: every label in it cites a Figma node,
 * and its header can say "if you type 'Advance Pending' anywhere else, that is
 * the bug". Nothing equivalent exists for quotes. The file
 * `02 Inquiry Pipeline` contains exactly one quote artefact —
 * `Record/LineItemsTable` (12:7841), a TAB BODY with line items and a totals
 * strip — and no list, no builder, no status pill and no colour for any of the
 * five `quotes.status` members.
 *
 * So this module follows the SHAPE of `domain/stages.ts` (one map, no component
 * hardcodes a label, a test pins the enum against the API) while being honest
 * that the CONTENT is authored. What is NOT authored is the colour: every tone
 * below is an EXISTING token pair from `tokens.css`, chosen and measured, never
 * a new hex. Hard rule 1 is satisfied structurally anyway — `app.css` deletes
 * Tailwind's palette, so a colour outside the collection cannot be spelled.
 *
 * ===========================================================================
 * WHY EACH STATUS DRAWS THE PAIR IT DRAWS
 * ===========================================================================
 *  · `draft` — NEUTRAL. A draft has no outcome yet, and the design colours no
 *    such thing. It gets the chip treatment `Chip` already uses (bg/subtle +
 *    border/default ring) rather than a tint, because tinting it would make an
 *    unfinished document look like a state somebody reached.
 *  · `sent` — `status/info`. The document is out and nothing has come back. An
 *    informational fact, not an outcome.
 *  · `confirmed` — `status/success`. The one status that means the business won
 *    the work, and the one the sales order is snapshotted from.
 *  · `lost` — `stage/lost`. THE SAME WORD ALREADY HAS A COLOUR IN THIS APP
 *    (`STAGES.lost.tone`). Painting a lost quote a different red from a lost
 *    inquiry would say the two mean different things. They do not.
 *  · `expired` — `status/warning`. A quote whose `valid_till` has passed is a
 *    caution — the price may no longer hold — and NOT a loss. Nothing in Phase
 *    3a writes this status (no route moves a quote to `expired`; the lifecycle
 *    is `draft → sent → confirmed | lost`), so it is painted so a row that
 *    arrives from a later phase or the Phase 6 ETL cannot render a blank pill.
 *
 * ===========================================================================
 * THE NEUTRAL CHIP USES `text/primary`, NOT `text/secondary`, AND THAT IS A FIX
 * ===========================================================================
 * `STAGES`' own `neutral` tone is `bg-subtle text-fg-secondary ring-1 ring-line`.
 * MEASURED, `text/secondary` #6e7580 on `bg/subtle` #f7f9fb is **4.40:1** in
 * light mode — under AA for the 12px text a pill carries. That is not a new
 * discovery: `features/admin/admin-users.contrast.test.ts` already pins the pair
 * as "THE PAIR THIS SLICE REFUSED", and the users-admin screens route around it.
 *
 * Reproducing it here would ship a fourth screen with the same 4.40:1 label. So
 * the neutral quote chip keeps the geometry and swaps the foreground to
 * `text/primary` — 16.14:1 light, 15.11:1 dark. It reads quiet because it has no
 * HUE, not because it is faint. The pre-existing `STAGES.neutral` pill is NOT
 * touched: it is B4's file and its own screens, and widening this fix into it is
 * a change with its own before/after evidence, reported rather than taken.
 */

/**
 * `quotes.status`, in DDL order (`apps/api/src/entities/quote.entity.ts:17`).
 * Declared here rather than imported so the browser bundle carries no dependency
 * on the API package; `quotes.test.ts` asserts this array against that file.
 */
export const QUOTE_STATUSES = ['draft', 'sent', 'confirmed', 'lost', 'expired', 'annulled'] as const;

export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

/** `sales_orders.status` (`apps/api/src/entities/sales-order.entity.ts:17`). */
export const SALES_ORDER_STATUSES = [
  'pending_push',
  'pushed',
  'invoiced',
  'cancelled',
] as const;

export type SalesOrderStatus = (typeof SALES_ORDER_STATUSES)[number];

/** `discount_requests.status` (`apps/api/src/entities/discount-request.entity.ts:16`). */
export const DISCOUNT_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const;

export type DiscountRequestStatus = (typeof DISCOUNT_REQUEST_STATUSES)[number];

/**
 * Which EXISTING token pair paints a document status.
 *
 * Named after the token family, not after a feeling, so the map is auditable
 * against `tokens.css` by reading it. `lost` and `confirm` are the `stage/*`
 * family; `info`/`success`/`warning` are the `status/*` family; `neutral` is the
 * design's own uncoloured chip treatment.
 */
export type DocTone = 'neutral' | 'info' | 'success' | 'warning' | 'lost' | 'confirm';

export interface DocStatusDescriptor<S extends string> {
  readonly status: S;
  /** The word on the pill and in the tab. */
  readonly label: string;
  readonly tone: DocTone;
  /**
   * One sentence a human can read when the label alone is not enough — the
   * pill's `title`, and the record page's status line. `null` where the label
   * says everything.
   */
  readonly meaning: string | null;
  /** Can Phase 3a actually produce this status? See the file header. */
  readonly reachableIn3a: boolean;
}

/**
 * WHAT KIND OF DOCUMENT THIS IS (§B1) — the label, in one place.
 *
 * Deliberately NOT a `DocStatusDescriptor`: the type carries no tone. A quote's
 * colour belongs to its STATUS, and a document that is both `draft` and
 * `tally_sales` must not shout twice. The tag renders in the neutral secondary
 * text token, which the contrast suite already measures on `bg/surface`.
 *
 * The mirror rule (see the file header): `QuoteType` lives in `features/quotes/api.ts`
 * alongside the wire DTO it belongs to; this map is keyed by it so a new member
 * of the union cannot be added without giving it a label here.
 *
 * ===========================================================================
 * ⚠ THE CLOSED `Record` DID ITS JOB. THIS IS WHAT IT CAUGHT.
 * ===========================================================================
 * Migration 47 added `mixed`, and this map is written as a `Record` over the literal
 * union PRECISELY so that a third member is a compile error until somebody writes the
 * word a human will read. It was, at three sites: `QuoteRecordPage.tsx` twice (the record
 * header and the Type row) and `QuotesListPage.tsx` once (every worklist row's second
 * line). All three would otherwise have rendered `undefined` beside a document number.
 *
 * `Mixed (Tally + CRF)` rather than the bare `Mixed`, and the choice is stated because no
 * ruling names the label. Every one of these renders lands MID-SENTENCE in the list's row
 * grammar — `docNo · type · raised …` — where "Mixed" alone would read as a state of the
 * document rather than a description of what is on it. The parenthesis says which two
 * things, which is the entire content of the word. It stays SENTENCE CASE with `Tally`
 * capitalised as the product name, so `statusLabelInSentence`'s rule still holds.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠ REWRITTEN IN THIS PASS (rule 5) — THE CLAIM ABOVE IT HAD STOPPED BEING TRUE.
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS: *"NOTHING PRODUCES A MIXED DOCUMENT YET (the create route refuses the value; the
 * derivation is the next unit), so this label is currently unreachable on every screen. It
 * is written now because the alternative was to write it in the same commit that makes
 * documents mixed, which is when nobody is looking at the words."*
 *
 * Both halves are now false, and the commit that falsified them (C11/IQ-12b) edited this
 * very file. The create route no longer refuses the value — it DERIVES it, and refuses a
 * client that states one — and the derivation is not the next unit, it is
 * {@link deriveQuoteType} thirty lines below. `mixed` is the ordinary answer for a
 * compliant Angolan or Congolese Tally document, because the government patch those
 * countries mandate is not a licence line.
 *
 * WHAT IS TRUE NOW: the label SHIPS. `QuoteBuilderPage.tsx` renders
 * `QUOTE_TYPE_LABELS[derivedType]` for the type it derives from the chosen lines, so an
 * operator reads this exact string while building the document. The record header, the
 * Type row and the worklist row render whatever the server stored, `mixed` included.
 * The one arm still unreached is `QuoteTypeTag`'s — for the unrelated reason that no
 * surface renders that component at all (see `quote-type-tag.test.tsx`).
 */
export const QUOTE_TYPE_LABELS: Readonly<Record<'crf' | 'tally_sales' | 'mixed', string>> = {
  crf: 'CRF customisation',
  tally_sales: 'Tally Sales',
  mixed: 'Mixed (Tally + CRF)',
};

export const QUOTE_STATUS_MAP: Readonly<
  Record<QuoteStatus, DocStatusDescriptor<QuoteStatus>>
> = {
  draft: {
    status: 'draft',
    label: 'Draft',
    tone: 'neutral',
    meaning: 'Not yet sent to the customer. Lines and discounts can still change.',
    reachableIn3a: true,
  },
  sent: {
    status: 'sent',
    label: 'Sent',
    tone: 'info',
    meaning: 'Issued to the customer. The lines are frozen.',
    reachableIn3a: true,
  },
  confirmed: {
    status: 'confirmed',
    label: 'Confirmed',
    tone: 'success',
    /*
     * SPLIT IN N4.6. This sentence used to end "…A sales order was created from this
     * quote." That was true when every quote was a CRF quote; it is FALSE for a
     * `tally_sales` quote, which confirms and stops (§B5).
     *
     * The half that survives is the half that is a property of the STATUS, which is
     * all this map is entitled to describe. What confirming CAUSED depends on the
     * document type, so the record page says that part — it is the only surface that
     * knows the type. The list's status-pill tooltip reads this string too, and it
     * now says only what is true of every confirmed quote in every country.
     */
    meaning: 'The customer accepted. The lines are final.',
    reachableIn3a: true,
  },
  lost: {
    status: 'lost',
    label: 'Lost',
    tone: 'lost',
    // The reason is MANDATORY at the API (422 LOST_REASON_REQUIRED) and — OQ-U3-3
    // — `quotes` has no `lost_reason` column, so it lives only in the event
    // payload. The record page says exactly that rather than showing a blank.
    meaning: 'Closed without a sale.',
    reachableIn3a: true,
  },
  expired: {
    status: 'expired',
    label: 'Expired',
    tone: 'warning',
    meaning: 'Validity lapsed. The prices in it are no longer guaranteed.',
    // No Phase 3a route writes this. See the file header.
    reachableIn3a: false,
  },
  // AGT design §3 U2, D7 — `POST /quotes/:id/annul`, from `sent`, in a SAF-T(AO) country only.
  annulled: {
    status: 'annulled',
    label: 'Annulled',
    tone: 'lost',
    meaning: 'Voided after it was issued. It keeps its number, and nothing more can happen to it.',
    reachableIn3a: true,
  },
};

/**
 * The four status tabs the quote list shows, in lifecycle order.
 *
 * `expired` is deliberately absent: it is an enum member no route can produce in
 * this phase, so a tab for it would be a permanently-empty tab that reads as a
 * broken filter. A quote that somehow HOLDS that status still renders — the "All"
 * tab has no `?status=` filter and the pill map is total over the enum.
 */
export const QUOTE_TABS: readonly QuoteStatus[] = ['draft', 'sent', 'confirmed', 'lost'];

export const SALES_ORDER_STATUS_MAP: Readonly<
  Record<SalesOrderStatus, DocStatusDescriptor<SalesOrderStatus>>
> = {
  pending_push: {
    status: 'pending_push',
    label: 'Awaiting Tally sync',
    /*
     * `info`, NOT `warning`, and NOT a spinner.
     *
     * This is the one sales-order status Phase 3a can produce, and the brief is
     * explicit that it must be shown honestly. Amber would say "something needs
     * attention"; a spinner would say "something is happening". Neither is true:
     * the push is Phase 3b and no code is running. Blue states a fact.
     */
    tone: 'info',
    meaning: 'Not yet pushed to Tally — the write path is Phase 3b.',
    reachableIn3a: true,
  },
  pushed: {
    status: 'pushed',
    label: 'Pushed to Tally',
    tone: 'success',
    meaning: 'The agent created the voucher in Tally.',
    reachableIn3a: false,
  },
  invoiced: {
    status: 'invoiced',
    label: 'Invoiced',
    // `stage/confirm` violet — a DIFFERENT green would have been unreadable
    // beside `pushed`, and this is a further step, not a repeat of that one.
    tone: 'confirm',
    meaning: 'Tally reported an invoice number back against this order.',
    reachableIn3a: false,
  },
  cancelled: {
    status: 'cancelled',
    label: 'Cancelled',
    tone: 'lost',
    meaning: null,
    reachableIn3a: false,
  },
};

/**
 * Tailwind class pairs for a status PILL, one per tone.
 *
 * Whole literal strings, never composed: Tailwind v4 scans source for complete
 * class names, so `` `bg-${tone}-bg` `` would compile to a pill with no colour.
 * Same rule, same reason, as `features/inquiries/stage-rail.ts`.
 */
const PILL_CLASSES: Readonly<Record<DocTone, string>> = {
  // 16.14:1 light / 15.11:1 dark. The ring is the boundary in dark mode, where
  // bg/subtle and bg/surface are the same #1f1f1f (tokens.css, dark-mode note).
  neutral: 'bg-subtle text-fg ring-1 ring-line',
  info: 'bg-info-bg text-info-fg', //    4.53:1, mode-invariant
  success: 'bg-ok-bg text-ok-fg', //     4.54:1, mode-invariant
  warning: 'bg-warn-bg text-warn-fg', // 4.51:1, mode-invariant
  lost: 'bg-stage-lost-bg text-stage-lost-fg', //       5.29:1, mode-invariant
  confirm: 'bg-stage-confirm-bg text-stage-confirm-fg', // 5.98:1, mode-invariant
};

export function docToneClasses(tone: DocTone): string {
  return PILL_CLASSES[tone];
}

/**
 * Tailwind classes for the L3 SPINE — the 3px rail down the left of a record.
 *
 * The light/dark flip is the ruling `features/inquiries/stage-rail.ts` measured
 * and this file re-measures for its own pairs: a bare rail renders ONE colour,
 * and the saturated `…-fg` tokens are mode-invariant, so on the #1f1f1f dark
 * surface they sink. MEASURED against `bg/surface` in dark:
 *
 *   stage/confirm/text 2.31 · stage/lost/text 2.54 · status/danger/text 2.98
 *     — all three UNDER the 3:1 WCAG 1.4.11 floor for a meaningful non-text element
 *   status/info/text 3.06 · status/success/text 3.23 · status/warning/text 3.25
 *     — over the floor, by less than 0.3
 *
 * Light takes `…-fg` (5.06 – 7.10), dark takes `…-bg` (13.49 – 14.70). The exact
 * figures are pinned in `features/quotes/quotes.contrast.test.ts`.
 *
 * `neutral` needs no `dark:` half: `text/secondary` is one of the SEVEN
 * variables Figma authors per mode (4.64 light / 5.85 dark).
 */
const RAIL_CLASSES: Readonly<Record<DocTone, string>> = {
  neutral: 'bg-fg-secondary',
  info: 'bg-info-fg dark:bg-info-bg',
  success: 'bg-ok-fg dark:bg-ok-bg',
  warning: 'bg-warn-fg dark:bg-warn-bg',
  lost: 'bg-stage-lost-fg dark:bg-stage-lost-bg',
  confirm: 'bg-stage-confirm-fg dark:bg-stage-confirm-bg',
};

export function docRailClass(tone: DocTone): string {
  return RAIL_CLASSES[tone];
}

/**
 * The rail's geometry, shared by the quote record and the sales-order record so
 * the two are one object rendered twice. Same literal, same reason, as
 * `RAIL_GEOMETRY` in `features/inquiries/stage-rail.ts`: a border width is not a
 * colour, a spacing step or a duration, and the `space/*` scale is 4·n with no 3.
 */
export const DOC_RAIL_GEOMETRY = 'w-[3px] shrink-0';

/**
 * A quote nobody can move any further.
 *
 * PROMOTED from `QuotesListPage.tsx` (where it was a file-local predicate) at
 * WT3, because the WORKLIST banding (`features/quotes/worklist.ts`) and the
 * page both need the same answer to "is this document finished?" — and two
 * spellings of the closed set is how `expired` gets dropped from one of them.
 * The set is the lifecycle's terminal statuses: `confirmed` and `lost` are the
 * two ends Phase 3a writes, and `expired` — reachable only from a later phase
 * or the ETL — is closed for the same reason (nothing moves it onward). `annulled` (AGT U2,
 * D7) is terminal too.
 */
export function isQuoteClosed(status: QuoteStatus): boolean {
  return status === 'confirmed' || status === 'lost' || status === 'expired' || status === 'annulled';
}

/*
 * THE AGT DOCUMENT NUMBER (AGT design G1/G3) — the mirror of `isAgtDocNo` in
 * `apps/api/src/modules/series/series.service.ts`, held to its patterns by `quotes.test.ts`.
 * Only the AGT freeze writes such a number, so a sent quote carrying one is an AGT document
 * (annullable, unchangeable); a legacy `AO-QT/…` or a `DRAFT-<id>` has no space and is not.
 */
export const AGT_DOC_NO_DE_74_19 = /^[A-Za-z0-9./_-]+ [A-Za-z0-9]*\/[0-9]+$/;
export const AGT_DOC_NO_XSD = /^[^ ]+ [^/^ ]+\/[0-9]+$/;
export const AGT_DOC_NO_MAX_LENGTH = 30;

export function isAgtDocNo(docNo: string): boolean {
  return (
    AGT_DOC_NO_DE_74_19.test(docNo) &&
    AGT_DOC_NO_XSD.test(docNo) &&
    docNo.length <= AGT_DOC_NO_MAX_LENGTH
  );
}

/** Narrow an API string (or a URL query) to a quote status. */
export function isQuoteStatus(value: unknown): value is QuoteStatus {
  return typeof value === 'string' && (QUOTE_STATUSES as readonly string[]).includes(value);
}

export function isSalesOrderStatus(value: unknown): value is SalesOrderStatus {
  return (
    typeof value === 'string' && (SALES_ORDER_STATUSES as readonly string[]).includes(value)
  );
}

export function quoteStatusLabel(status: QuoteStatus): string {
  return QUOTE_STATUS_MAP[status].label;
}

export function salesOrderStatusLabel(status: SalesOrderStatus): string {
  return SALES_ORDER_STATUS_MAP[status].label;
}

/**
 * A status label as it reads MID-SENTENCE: "No orders are pushed to Tally — …".
 *
 * NOT `label.toLowerCase()`. `"Pushed to Tally".toLowerCase()` is
 * `"pushed to tally"`, and TALLY IS A PRODUCT — the sales-order empty card
 * shipped "No orders are pushed to tally" directly under a tab reading "Pushed
 * to Tally", so one render contradicted itself. `pending_push` ("Awaiting Tally
 * sync") — the ONE sales-order status Phase 3a can actually produce — folded the
 * same way.
 *
 * The labels in both status maps are written in SENTENCE CASE, so the only
 * capital that is there by convention is the first one; every other capital is
 * there because the word is a proper noun. Folding the first character and
 * nothing else therefore preserves every proper noun in both maps WITHOUT this
 * function knowing that "Tally" exists — a status added later is covered by the
 * rule rather than by an edit here, which is the whole point of not special-casing
 * the one product name we happen to ship today.
 *
 * BOUNDARY, stated rather than hidden: a label whose FIRST word is itself a
 * proper noun ("Tally rejected") would still fold. No label in either map is one,
 * and `quotes.test.ts` pins the sentence form of ALL TEN statuses (nine until AGT U2's `annulled`) against a
 * hand-written table — so such a label arrives as a red test, not as shipped copy.
 */
export function statusLabelInSentence(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/**
 * Whole days since an ISO instant, or `null` when it is absent or unparseable.
 *
 * The list's "days" column. `null` renders as an em dash and NEVER as 0 — the
 * same ruling `daysInStage` makes, for the same reason: claiming "today" when
 * the truth is unknown misstates the one signal the column exists to carry.
 *
 * Deliberately NOT `daysInStage`: that function is about `stage_entered_at` and
 * a quote has no stage. Reusing its name here would make a later reader think
 * quotes have stages. The arithmetic is four lines and the meaning is different.
 */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return null;
  const elapsed = now.getTime() - at;
  if (elapsed < 0) return 0;
  return Math.floor(elapsed / 86_400_000);
}

/**
 * THE DOCUMENT'S KIND, DERIVED FROM ITS LINES — a MIRROR of
 * `apps/api/src/modules/quotes/derive-quote-type.ts` (owner rulings R1 / Q3).
 *
 * ===========================================================================
 * ⚠ THE SERVER DECIDES. THIS EXISTS SO THE BUILDER CAN SHOW THE ANSWER WHILE IT IS
 *   STILL BEING BUILT, AND IT PUTS NOTHING ON THE WIRE.
 * ===========================================================================
 * The builder used to ASK for the type with a radio; the radio is gone and the request
 * carries no `type` at all. So unlike `missingPatchReason` — whose divergence from the
 * server is a stated, deliberate widening — this one has no safe-direction argument to
 * make, because it cannot cause or prevent anything. The worst a divergence could do is
 * show a label the server disagrees with, for as long as it takes to save the document
 * and read the stored value back.
 *
 * That makes it worth keeping honest rather than worth guarding: `quotes.test.ts` pins it
 * against the API source, so the two cannot drift silently.
 *
 * THE RULE:
 *   · any `tally_licence` kind AND any other kind → `mixed`
 *   · every kind a `tally_licence`                → `tally_sales`
 *   · no `tally_licence` kind, none at all        → `crf`
 *
 * ⚠ AN EMPTY BASKET IS `crf`, AND SO IS A HALF-FILLED ONE. A line whose product has not
 * been picked contributes no kind, so a blank builder reads "CRF customisation" rather
 * than blank — which is the truthful answer (a document selling no Tally licence is a CRF
 * quote), not a placeholder.
 *
 * The return type is the literal union rather than `QuoteType` imported from
 * `features/quotes/api.ts` — this module imports nothing, the mirror rule the file header
 * states — so a fourth member is a compile error here and must be ruled on.
 */
export function deriveQuoteType(
  lineKinds: readonly string[],
): 'crf' | 'tally_sales' | 'mixed' {
  const sellsALicence = lineKinds.some((kind) => kind === 'tally_licence');
  if (!sellsALicence) return 'crf';
  const sellsSomethingElse = lineKinds.some((kind) => kind !== 'tally_licence');
  return sellsSomethingElse ? 'mixed' : 'tally_sales';
}

/**
 * §B2 — the countries whose licence terms make the government patch MANDATORY.
 *
 * A MIRROR of `PATCH_MANDATORY_COUNTRIES` in
 * `apps/api/src/modules/quotes/patch-requirement.service.ts`, kept as a local literal
 * because `@crf/shared` is CommonJS and this app may only `import type` from it.
 *
 * ⚠ THE SERVER IS THE AUTHORITY. This list exists so the builder can SHOW the
 * requirement as a required line rather than letting the operator discover it as a 422
 * at save (§N5: "not a surprise 422 at save"). If the two ever disagree the server
 * still refuses, which is the safe direction — a UI that permitted something the API
 * forbids produces one wasted round trip, whereas a UI that enforced something the API
 * allowed would block real work with no way past it.
 */
export const PATCH_MANDATORY_COUNTRIES: readonly string[] = ['AO', 'CD'];

/**
 * The missing-patch sentence for this basket, or `null` when nothing is missing.
 *
 * ===========================================================================
 * ⚠ THIS IS NOT PARITY WITH THE SERVER, AND SAYING SO WAS THE DEFECT (D-1)
 * ===========================================================================
 * This comment used to claim the mirror reproduced the server rule "exactly,
 * INCLUDING what triggers it: the presence of a `tally_licence` LINE, not the
 * quote's type". That was true when it was written and stopped being true when
 * `patch-requirement.service.ts` RE-ANCHORED the rule on `quotes.type` — the
 * `if (!isTallySales) return;` guard in `assertPatchRequirement`, and the
 * argument above it ("a catalogue entry someone can label is a catalogue entry
 * someone can MIS-label").
 *
 * The claim was load-bearing, so its going stale was not cosmetic. A
 * `tally_sales` quote in AO/CD with no licence line is refused 422
 * `PATCH_REQUIRED` by the API; this mirror returned `null`; and §N5's promise
 * — "not a surprise 422 at save" — was broken in production for exactly the
 * documents the licence condition exists to govern. Owner ruling Q11 fixes it
 * here. What follows is therefore a STATED DIVERGENCE, not a parity claim.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠ THE DIVERGENCE IS CLOSED IN C11/IQ-12b — BY THE EXACT ROUTE THIS DOCBLOCK
 *   NAMED, AND BECAUSE A PLANTED BREAK PROVED IT HAD TO BE.
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT STOOD HERE: the server refused `AO/CD ∧ type = 'tally_sales' ∧ no gov_patch`,
 * this mirror ALSO blocked when `a tally_licence line is present`, and the second
 * disjunct was kept deliberately wider — *"a CRF-typed quote selling a Tally licence
 * in Angola is ACCEPTED by the API and BLOCKED here … a client stricter than the
 * server costs the operator one blocked basket."* The instruction attached to it was
 * explicit: *"If the divergence is ever closed, close it by DELETING the second
 * disjunct AFTER the server's own anchor is proven sound in AO/CD."*
 *
 * BOTH CONDITIONS ARE NOW MET, and neither is a claim:
 *  · The anchor is sound. `quotes.type` is DERIVED from the lines by the server
 *    (`derive-quote-type.ts`), and the negative it rests on — "no `tally_licence`
 *    kind" — became trustworthy when `unclassified` became the column default and
 *    an unclassified product stopped being able to reach a customer document.
 *  · The two disjuncts are now EQUIVALENT, not merely both true. `deriveQuoteType`
 *    answers `tally_sales` or `mixed` for exactly the baskets that contain a
 *    `tally_licence` kind — over the SAME list this function is handed. So
 *    `typeCarriesTheCondition ⟺ chosenKinds.includes('tally_licence')`.
 *
 * ⚠ AND KEEPING THE SECOND DISJUNCT WOULD HAVE BEEN WORSE THAN REDUNDANT. It made
 * the caller's argument unfalsifiable: a planted break that hard-wired the call site
 * to `'crf'` left every test in both client suites GREEN, because the licence line
 * answered the question the type was supposed to answer. The wiring probe this
 * function's own history exists to protect could no longer fail its premise. One
 * disjunct restores it.
 *
 * THE RULE, now a parity claim and written as one:
 *   AO/CD ∧ (`type` carries the condition) ∧ no `gov_patch` line.
 *
 * `quoteType` is typed as the literal union rather than imported from
 * `features/quotes/api.ts` — this module imports nothing, the mirror rule the
 * file header states — so a third member (`mixed`, C10/IQ-12a) is a COMPILE
 * ERROR here and must be ruled on rather than defaulted.
 *
 * ===========================================================================
 * ⚠ THAT COMPILE ERROR HAPPENED, AND HERE IS THE RULING IT FORCED
 * ===========================================================================
 * `mixed` (migration 47) COUNTS AS A TALLY SALE for this rule, exactly as the
 * server's own `patchConditionApplies` decides — a mixed document is mixed
 * BECAUSE it carries a Tally licence, and Angola may sell one only bundled with
 * the government patch. Note that on this mirror the left disjunct is
 * belt-and-braces: a mixed basket contains a `tally_licence` line by
 * construction, so the second disjunct already blocked it. Stating the type arm
 * anyway keeps this function readable as the same rule the server enforces
 * instead of one that happens to agree on today's data.
 *
 * ⚠ THAT ARM IS LIVE NOW (C11/IQ-12b), AND IT IS THE COMMON CASE IN AO/CD. It
 * used to say *"NO BASKET CAN BE MIXED IN THE COMMIT THAT ADDS THE VALUE — the
 * builder offers two types and the API refuses a third"*. The builder offers
 * nothing: `deriveQuoteType` computes the type from the lines, and because
 * Angola and DR Congo may sell a licence only bundled with a `gov_patch` line —
 * which is not a licence line — every compliant Angolan Tally basket derives
 * `mixed`. The arm that was written to avoid deciding a negative by accident
 * turns out to carry almost all the traffic the rule governs.
 */
export function missingPatchReason(
  countryCode: string,
  quoteType: 'crf' | 'tally_sales' | 'mixed',
  chosenKinds: readonly string[],
  patchesOnSale: readonly string[],
  /**
   * The quote's chosen currency, where the country has a choice (2026-09-22). `patchesOnSale`
   * is then read from THAT list, and an empty one is worded the way the server words it:
   * the patch has no price in this currency — not "no patch on the price list" while a CDF
   * one exists. Omitted or null keeps the one-list wording.
   */
  currency: string | null = null,
): string | null {
  if (!PATCH_MANDATORY_COUNTRIES.includes(countryCode)) return null;
  // Satisfied: the patch is on the document. Checked FIRST because it answers
  // both disjuncts below at once, and because it is the only way out.
  if (chosenKinds.includes('gov_patch')) return null;
  // THE ARM D-1 WAS MISSING, and it is now the WHOLE rule (see the docblock). A
  // `tally_sales` document needs the patch whatever its lines say — including when
  // it has none yet. `mixed` joins it, named rather than folded into a `!== 'crf'`
  // so that a FOURTH member does not silently inherit the answer.
  const typeCarriesTheCondition = quoteType === 'tally_sales' || quoteType === 'mixed';
  if (!typeCarriesTheCondition) return null;
  if (patchesOnSale.length === 0) {
    if (currency !== null) {
      return `${countryCode} may sell a Tally licence only with its government patch, and no government patch has a ${currency} price in ${countryCode}. Ask an administrator to set one.`;
    }
    return `${countryCode} may sell a Tally licence only with its government patch, and no patch product is on this country's price list. Ask an administrator to add it.`;
  }
  return `${countryCode} may sell a Tally licence only bundled with its government patch. Add ${patchesOnSale.join(' or ')} as a line.`;
}
